<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { getInboxVoiceIcon } from 'dashboard/helper/inbox';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AudioPlayer from 'dashboard/components-next/audio/AudioPlayer.vue';
import CallStatusBadge from './CallStatusBadge.vue';
import { CALL_KIND, getCallKind } from './constants';

const props = defineProps({
  call: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();

const kind = computed(() => getCallKind(props.call));

const contactName = computed(
  () => props.call.contact.name || props.call.contact.phoneNumber || ''
);

const durationLabel = computed(() => {
  const seconds = props.call.durationSeconds;
  if (!seconds) return '—';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
});

const directionLabel = computed(() =>
  t(`CALLS_PAGE.STATUS.${kind.value.toUpperCase()}`)
);

const startedAtLabel = computed(() =>
  format(new Date(props.call.createdAt * 1000), 'MMM d, HH:mm')
);

const subtitle = computed(() =>
  [props.call.contact.phoneNumber, directionLabel.value, startedAtLabel.value]
    .filter(Boolean)
    .join(' · ')
);

const providerIcon = computed(() =>
  getInboxVoiceIcon(props.call.inbox.channelType, props.call.inbox.medium)
);

// Only connected calls can have an agent; missed ones show the reason instead.
const outcomeLabel = computed(() => {
  if (kind.value === CALL_KIND.MISSED) return t('CALLS_PAGE.ROW.NO_AGENT');
  if (kind.value === CALL_KIND.NO_REPLY) {
    return t('CALLS_PAGE.ROW.NO_CONTACT_ANSWER');
  }
  if (kind.value === CALL_KIND.FAILED) return t('CALLS_PAGE.ROW.FAILED');
  return t('CALLS_PAGE.ROW.ANSWERED');
});

const conversationRoute = computed(() => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: props.call.conversation.displayId,
  },
  query: { messageId: props.call.messageId },
}));
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex items-center gap-3 px-6 py-4 border-b shrink-0 border-n-weak"
    >
      <Avatar
        :src="call.contact.avatar"
        :name="contactName"
        :size="44"
        rounded-full
      />
      <div class="flex flex-col min-w-0 gap-0.5">
        <div class="flex items-center gap-2 min-w-0">
          <h2 class="mb-0 truncate text-heading-2 text-n-slate-12">
            {{ contactName }}
          </h2>
          <CallStatusBadge :kind="kind" class="shrink-0" />
        </div>
        <span class="truncate text-label-small text-n-slate-11 tabular-nums">
          {{ subtitle }}
        </span>
      </div>
      <RouterLink :to="conversationRoute" class="ms-auto shrink-0">
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-message-circle"
          :label="
            t('CALLS_PAGE.DETAIL.OPEN_CONVERSATION', {
              id: call.conversation.displayId,
            })
          "
        />
      </RouterLink>
    </header>

    <div class="flex flex-col gap-6 px-6 py-5 overflow-y-auto grow">
      <AudioPlayer
        v-if="call.recordingUrl"
        :src="call.recordingUrl"
        :fallback-duration="call.durationSeconds || 0"
        class="w-full"
      />
      <div
        v-else
        class="flex items-center gap-2 px-4 py-3 rounded-lg bg-n-alpha-1 text-n-slate-11"
      >
        <Icon icon="i-lucide-mic-off" class="size-4 shrink-0" />
        <span class="text-sm">{{ t('CALLS_PAGE.DETAIL.NO_RECORDING') }}</span>
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="mb-0 text-label-small uppercase text-n-slate-10">
          {{ t('CALLS_PAGE.DETAIL.TRANSCRIPT') }}
        </h3>
        <p
          v-if="call.transcript"
          class="mb-0 text-sm whitespace-pre-line text-n-slate-12"
        >
          {{ call.transcript }}
        </p>
        <p v-else class="mb-0 text-sm text-n-slate-10">
          {{ t('CALLS_PAGE.DETAIL.NO_TRANSCRIPT') }}
        </p>
      </div>

      <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1">
        <h3 class="mb-0 text-label-small uppercase text-n-slate-10">
          {{ t('CALLS_PAGE.DETAIL.DETAILS') }}
        </h3>
        <dl class="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2 text-sm">
          <dt class="text-n-slate-11">{{ t('CALLS_PAGE.DETAIL.LINE') }}</dt>
          <dd class="flex items-center gap-1.5 m-0 min-w-0 text-n-slate-12">
            <Icon :icon="providerIcon" class="size-4 shrink-0" />
            <span class="truncate">{{ call.inbox.name }}</span>
          </dd>
          <dt class="text-n-slate-11">{{ t('CALLS_PAGE.DETAIL.OUTCOME') }}</dt>
          <dd class="m-0 text-n-slate-12">{{ outcomeLabel }}</dd>
          <template v-if="call.agent">
            <dt class="text-n-slate-11">
              {{ t('CALLS_PAGE.DETAIL.HANDLED_BY') }}
            </dt>
            <dd class="flex items-center gap-1.5 m-0 min-w-0 text-n-slate-12">
              <Avatar
                :src="call.agent.avatar"
                :name="call.agent.name"
                :size="20"
                rounded-full
              />
              <span class="truncate">{{ call.agent.name }}</span>
            </dd>
          </template>
          <dt class="text-n-slate-11">{{ t('CALLS_PAGE.DETAIL.DURATION') }}</dt>
          <dd class="m-0 tabular-nums text-n-slate-12">{{ durationLabel }}</dd>
        </dl>
      </div>
    </div>
  </section>
</template>
